import DayOutlet from "@app/secured/CourseDayPageV2/components/DayOutlet";
import { useAuth0 } from "@auth0/auth0-react";
import { Auth0Provider } from "@contexts/access";
import { AccessProvider } from "@contexts/access/AccessContext";
import { Loader } from "@core/components";
import { RouteErrorFallback } from "@core/componentsV2";
import { storageKeys } from "@core/constants";
import { RequestStatus } from "@core/enums";
import { useHangfireCookiesCleaner } from "@core/hooks";
import SecuredLayout from "@layouts/secured";
import UnsecuredLayout from "@layouts/unsecured";
import { MockAuthService } from "@services";
import { lazy, useEffect, useMemo } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouteObject,
  RouterProvider,
  ScrollRestoration,
} from "react-router-dom";

const DashboardPage = lazy(() => import("@app/secured/DashboardPage"));
const LearningPage = lazy(() => import("@app/secured/LearningPage"));
const LearningPageV2 = lazy(() => import("@app/secured/LearningPageV2"));
const CourseHomePage = lazy(() => import("@app/secured/CourseHomePage"));
const CourseHomePageV2 = lazy(() => import("@app/secured/CourseHomePageV2"));
const TemplateHomePageV2 = lazy(() => import("@app/secured/TemplateHomePage"));
const CourseDayPage = lazy(() => import("@app/secured/CourseDayPage"));
const CourseDayPageV2 = lazy(() => import("@app/secured/CourseDayPageV2"));
const GradebookDayPageV2 = lazy(
  () => import("@app/secured/GradebookDayPageV2"),
);
const GradebookDayOutletV2 = lazy(
  () => import("@app/secured/GradebookDayPageV2/components/GradebookDayOutlet"),
);

const GradebookPage = lazy(() => import("@app/secured/GradebookPage"));
const GradebookPageV2 = lazy(() => import("@app/secured/GradebookPageV2"));
const GradebookDayPage = lazy(() => import("@app/secured/GradebookDayPage"));
const GradebookDayItemOutlet = lazy(
  () => import("@app/secured/GradebookDayPage/GradebookDayItemOutlet"),
);
const DayItemOutlet = lazy(
  () => import("@app/secured/CourseDayPage/components/DayItemOutlet"),
);
const RequestsPage = lazy(() => import("@app/secured/RequestsPage"));
const EnrollmentPage = lazy(() => import("@app/secured/EnrollmentPage"));
const EnrollmentPageV2 = lazy(() => import("@app/secured/EnrollmentPageV2"));
const LoginPage = lazy(() => import("@app/unsecured/LoginPage"));
const HangfirePage = lazy(() => import("@app/secured/HangfirePage"));

function withErrorBoundary(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => {
    const updatedRoute: RouteObject = {
      ...route,
      errorElement: <RouteErrorFallback />,
    };

    if (route.children && route.children.length > 0) {
      updatedRoute.children = withErrorBoundary(route.children);
    }

    return updatedRoute;
  });
}

// Routes available for authorized users only
export const securedRoutes: RouteObject[] = [
  {
    path: "/",
    element: <DashboardPage />,
  },
  {
    path: "courses",
    element: <LearningPage />,
  },
  {
    path: "courses/:tab",
    element: <LearningPage />,
  },
  {
    path: "courses-v2",
    element: <LearningPageV2 />,
  },
  {
    path: "courses-v2/:tab",
    element: <LearningPageV2 />,
  },
  {
    path: "courses/:tab/:courseId/:subtab?",
    element: <CourseHomePage />,
  },
  {
    path: "courses-v2/:tab/:courseId/:subtab?",
    element: <CourseHomePageV2 />,
  },
  {
    path: "courses-v2/templates/:templateId/:subtab?",
    element: <TemplateHomePageV2 />,
  },
  {
    path: "courses/:tab/:courseId/:subtab?/my-gradebook",
    element: <GradebookPage />,
  },

  {
    path: "courses/:tab/:courseId/:subtab?/my-gradebook/days/:dayId",
    element: <GradebookDayPage />,
    children: [
      {
        path: "content/:contentAreaId",
        element: <GradebookDayItemOutlet />,
      },
    ],
  },
  {
    path: "courses/:tab/:courseId/learners/:learnerId",
    element: <GradebookPage />,
  },
  {
    path: "courses-v2/:tab/:courseId/learners/:learnerId",
    element: <GradebookPageV2 />,
  },
  {
    path: "courses/:tab/:courseId/outline/days/:dayId",
    element: <CourseDayPage />,
    children: [
      {
        path: "content/:contentAreaId",
        element: <DayItemOutlet />,
      },
    ],
  },

  {
    path: "courses-v2/:tab/:courseId/outline/days/:dayId",
    element: <CourseDayPageV2 />,
    children: [
      {
        path: "activities/:activityId",
        element: <DayOutlet />,
      },
    ],
  },
  {
    path: "courses/:tab/:courseId/progress/days/:dayId",
    element: <CourseDayPage />,
    children: [
      {
        path: "content/:contentAreaId",
        element: <DayItemOutlet />,
      },
    ],
  },
  {
    path: "courses-v2/:tab/:courseId/progress/days/:dayId",
    element: <CourseDayPageV2 />,
    children: [
      {
        path: "activities/:activityId",
        element: <DayOutlet />,
      },
    ],
  },
  {
    path: "courses-v2/:tab/:courseId/ungraded/learners/:learnerId",
    element: <GradebookDayPageV2 mode="ungraded" />,
    children: [
      {
        path: "activities/:activityId/days/:dayId",
        element: <GradebookDayOutletV2 />,
      },
    ],
  },
  {
    path: "courses-v2/:tab/:courseId/my-gradebook/activities/:activityId",
    element: <GradebookDayPageV2 mode="my-gradebook" />,
    children: [
      {
        path: "days/:dayId",
        element: <GradebookDayOutletV2 />,
      },
    ],
  },
  {
    path: "courses-v2/:tab/:courseId/learners/:learnerId",
    element: <GradebookDayPageV2 mode="gradebook" />,
    children: [
      {
        path: "activities/:activityId/days/:dayId",
        element: <GradebookDayOutletV2 />,
      },
    ],
  },
  {
    path: "courses/:tab/:courseId/learners/:learnerId/days/:dayId",
    element: <GradebookDayPage />,
    children: [
      {
        path: "content/:contentAreaId",
        element: <GradebookDayItemOutlet />,
      },
    ],
  },
  {
    path: "courses/:tab/:courseId/ungraded/learners/:learnerId/days/:dayId",
    element: <GradebookDayPage />,
    children: [
      {
        path: "content/:contentAreaId",
        element: <GradebookDayItemOutlet />,
      },
    ],
  },
  {
    path: "courses/:tab/:courseId/learners/:learnerId/days/:dayId/gradebook/:gradebookId",
    element: <GradebookDayPage />,
  },
  {
    path: "courses/:tab/:courseId/:subtab?/my-gradebook/days/:dayId/gradebook/:gradebookId",
    element: <GradebookDayPage />,
  },
  {
    path: "courses/:tab/:courseId/ungraded/learners/:learnerId/days/:dayId/gradebook/:gradebookId",
    element: <GradebookDayPage />,
  },
  {
    path: "requests",
    element: <RequestsPage defaultTabValue={RequestStatus.Pending} />,
  },
  {
    path: "requests-history",
    element: <RequestsPage defaultTabValue={RequestStatus.Resolved} />,
  },
  {
    path: "enroll/:courseId",
    element: <EnrollmentPage />,
  },
  {
    path: "hangfire",
    element: <HangfirePage />,
  },
  { path: "courses-v2/active/:courseId/enroll", element: <EnrollmentPageV2 /> },
];

// Routes available for both authorized and unauthorized
export const securedAndUnsecuredRoutes: RouteObject[] = [
  {
    path: "*",
    element: <div>404 page was not found!</div>,
  },
];

// Route availabe only for unauthorized routes
export const unsecuredRoutes: RouteObject[] = [
  {
    path: "/",
    element: <LoginPage returnUrl={location.pathname} />,
  },
];

export const navigateToHomeRoute = (route: RouteObject) => ({
  ...route,
  element: <Navigate to="/" />,
});

export const CCAURouterProvider = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const isLoggedIn =
    isAuthenticated ||
    (MockAuthService.isEnabled && MockAuthService.isAuthorized);

  useHangfireCookiesCleaner();

  const router = useMemo(() => {
    if (isLoading) return null;

    const routesWithRedirectToHome = (
      isLoggedIn ? unsecuredRoutes : securedRoutes
    ).map(navigateToHomeRoute);

    const children = securedAndUnsecuredRoutes
      .concat(isLoggedIn ? securedRoutes : unsecuredRoutes)
      .concat(routesWithRedirectToHome);

    return createBrowserRouter([
      {
        path: "/",
        element: (
          <>
            <ScrollRestoration />
            {isLoggedIn ? (
              <Auth0Provider>
                <AccessProvider>
                  <SecuredLayout />
                </AccessProvider>
              </Auth0Provider>
            ) : (
              <UnsecuredLayout />
            )}
          </>
        ),
        children: withErrorBoundary(children),
      },
    ]);
  }, [isLoading, isLoggedIn]);

  useEffect(() => {
    const returnUrl = localStorage.getItem(storageKeys.returnUrl);

    if (
      !isLoading &&
      isAuthenticated &&
      returnUrl &&
      (returnUrl.includes("enroll") ||
        returnUrl.includes("dashboard") ||
        returnUrl.includes("courses") ||
        returnUrl.includes("requests"))
    ) {
      localStorage.removeItem(storageKeys.returnUrl);
      window.location.href = returnUrl;
    }
  }, [isAuthenticated, isLoading]);

  if (isLoading || !router) {
    return <Loader fullScreen />;
  }

  return (
    <RouterProvider
      fallbackElement={<div>Something went wrong</div>}
      router={router}
    />
  );
};

export default CCAURouterProvider;
